import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ImportLoadingSkeleton } from '../components/LoadingSkeletons';
import Footer from '../components/Footer';
import UsageMeter from '../components/UsageMeter';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Upload, Zap, Twitter, MessageSquare, Mail, FileSpreadsheet, Table } from 'lucide-react';
import { API_URL } from '../lib/apiBase';

const ImportFeedbackPage = () => {
  const navigate = useNavigate();
  const { currentWorkspace, currentWorkspaceId, refreshUsage } = useWorkspace();
  const [text, setText] = useState('');
  const [sourceType, setSourceType] = useState('auto');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleImport = async () => {
    if (!text.trim()) {
      toast.error('Please paste some feedback to import');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/api/feedback/import`, {
        text,
        source_type: sourceType,
        workspace_id: currentWorkspaceId,
      });

      setResult(response.data);
      toast.success(`✅ Imported ${response.data.signals_imported} signals!`);

      if (response.data.quick_wins > 0) {
        toast.success(`🎯 Found ${response.data.quick_wins} quick wins!`, {
          duration: 5000
        });
      }

      // Refresh usage so meter updates
      refreshUsage();

      // Clear form
      setText('');
    } catch (error) {
      console.error('Import error:', error);
      const errData = error.response?.data?.detail;
      if (error.response?.status === 429 && typeof errData === 'object') {
        toast.error(errData.message || 'Monthly limit reached', {
          description: `Upgrade to ${errData.upgrade_to || 'a paid plan'} for ${errData.upgrade_limit || 'more'} imports/month.`,
          action: {
            label: 'View plans',
            onClick: () => navigate('/pricing'),
          },
          duration: 8000,
        });
      } else {
        toast.error(typeof errData === 'string' ? errData : 'Failed to process signals');
      }
    } finally {
      setLoading(false);
    }
  };

  const exampleTemplates = {
    twitter: `@user1: Your pricing page is super confusing. Can't figure out which plan to choose.
@user2: Love the product! But really need a mobile app.
@user3: Been trying to export my data for 2 days. Export button doesn't work.`,
    
    discord: `[10:23] john: The onboarding flow is broken again
[10:25] sarah: +1 same issue here, can't get past step 2
[10:30] mike: Would pay extra for Slack integration`,
    
    email: `Subject: Feature Request - API Access

Hi team,

We're a 50-person company and need API access to integrate with our internal tools. Would happily upgrade to enterprise pricing for this.

Thanks!`,
    
    csv: `text,channel,user_segment
"Need dark mode for late night work",feedback,paid
"Pricing is too expensive for small teams",x,trial
"Love the new dashboard redesign!",instagram,enterprise`
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/app')}
                data-testid="import-back-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Add signals manually</h1>
                <p className="text-sm text-muted-foreground">
                  {currentWorkspace ? (
                    <>Adding to <span className="font-medium text-foreground">{currentWorkspace.icon} {currentWorkspace.name}</span> · or connect Signal Sources</>
                  ) : (
                    'Paste feedback from Twitter, Discord, Email, or CSV — or connect Signal Sources'
                  )}
                </p>
              </div>
            </div>
            <UsageMeter variant="compact" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
              <div>
                <h3 className="text-lg font-semibold mb-1">Analyzing feedback...</h3>
                <p className="text-sm text-muted-foreground">
                  Our AI is categorizing your signals into Build/Fix/Ignore
                </p>
              </div>
              <ImportLoadingSkeleton />
            </div>
          </div>
        ) : (
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Import Form */}
          <div className="space-y-6">
            <Card className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold mb-2 block">
                    Source Type
                  </label>
                  <Select value={sourceType} onValueChange={setSourceType}>
                    <SelectTrigger data-testid="source-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">
                        <div className="flex items-center gap-2">
                          <Zap className="h-4 w-4" />
                          Auto-Detect
                        </div>
                      </SelectItem>
                      <SelectItem value="twitter">
                        <div className="flex items-center gap-2">
                          <Twitter className="h-4 w-4" />
                          Twitter / X DMs
                        </div>
                      </SelectItem>
                      <SelectItem value="discord">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="h-4 w-4" />
                          Discord Messages
                        </div>
                      </SelectItem>
                      <SelectItem value="email">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          Email
                        </div>
                      </SelectItem>
                      <SelectItem value="csv">
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="h-4 w-4" />
                          CSV
                        </div>
                      </SelectItem>
                      <SelectItem value="table">
                        <div className="flex items-center gap-2">
                          <Table className="h-4 w-4" />
                          Table (Notion/Airtable)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-semibold mb-2 block">
                    Paste Feedback
                  </label>
                  <Textarea
                    placeholder="Paste your feedback here...\n\nExample:\nUser1: Your pricing is confusing\nUser2: Need a mobile app\nUser3: Love the product!"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={12}
                    className="font-mono text-sm"
                    data-testid="feedback-textarea"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    {text.length} characters
                  </p>
                </div>

                <Button
                  onClick={handleImport}
                  disabled={loading || !text.trim()}
                  className="w-full btn-cta"
                  size="lg"
                  data-testid="import-submit-button"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Analyze signals
                    </>
                  )}
                </Button>

                {result && (
                  <div className="mt-4 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                    <h3 className="font-semibold text-green-800 dark:text-green-400 mb-2">
                      ✅ Analysis complete!
                    </h3>
                    <div className="space-y-1 text-sm text-green-700 dark:text-green-300">
                      <p>• {result.signals_imported} signals added</p>
                      <p>• {result.decisions_created} decisions created</p>
                      {result.quick_wins > 0 && (
                        <p className="font-semibold">🎯 {result.quick_wins} quick wins found!</p>
                      )}
                    </div>
                    <Button
                      onClick={() => navigate('/app')}
                      variant="outline"
                      size="sm"
                      className="mt-3"
                    >
                      View in Dashboard →
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Examples & Help */}
          <div className="space-y-6">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Examples</h2>
              
              <Tabs defaultValue="twitter">
                <TabsList className="grid grid-cols-4 mb-4">
                  <TabsTrigger value="twitter" className="text-xs">Twitter</TabsTrigger>
                  <TabsTrigger value="discord" className="text-xs">Discord</TabsTrigger>
                  <TabsTrigger value="email" className="text-xs">Email</TabsTrigger>
                  <TabsTrigger value="csv" className="text-xs">CSV</TabsTrigger>
                </TabsList>
                
                {Object.entries(exampleTemplates).map(([key, template]) => (
                  <TabsContent key={key} value={key}>
                    <div className="space-y-3">
                      <pre className="p-4 bg-muted rounded-lg text-xs font-mono overflow-x-auto">
                        {template}
                      </pre>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setText(template)}
                        className="w-full"
                      >
                        Use This Example
                      </Button>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </Card>

            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-3">How It Works</h2>
              <ol className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <Badge variant="outline" className="text-xs">1</Badge>
                  <span>Paste feedback from any source (Twitter DMs, Discord, Email, CSV)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Badge variant="outline" className="text-xs">2</Badge>
                  <span>AI automatically parses and extracts individual signals</span>
                </li>
                <li className="flex items-start gap-2">
                  <Badge variant="outline" className="text-xs">3</Badge>
                  <span>Each signal is analyzed for revenue impact, churn risk, and effort</span>
                </li>
                <li className="flex items-start gap-2">
                  <Badge variant="outline" className="text-xs">4</Badge>
                  <span>Decisions appear instantly in your dashboard (Build/Fix/Ignore)</span>
                </li>
              </ol>
            </Card>

            <Card className="p-6 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
              <h3 className="font-semibold text-blue-800 dark:text-blue-400 mb-2">
                💡 Pro Tip
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                For best results, include user context like "paid user", "enterprise customer", or "trial user" in your feedback. This helps prioritize based on revenue impact.
              </p>
            </Card>
          </div>
        </div>
        )}
      </div>

      {/* Footer */}
      <Footer variant="minimal" />
    </div>
  );
};

export default ImportFeedbackPage;
